/**
 * <SocialPulseVerifiedBadge /> — a small verified-style badge that
 * signals "this identity was authenticated by SocialPulse".
 *
 * Rendered next to a user's display name on SISTER apps (FoodPulse,
 * IndianFamilyTree, TravelerPulse, MyFamilyTree.care, KeralaHealth) so
 * every profile touchpoint subtly evangelises the SocialPulse account.
 * Hidden on the SocialPulse hub itself — the mark would be meaningless
 * there (everyone is SocialPulse-native by definition).
 *
 * Usage:
 *   <SocialPulseVerifiedBadge user={u} />
 *   <SocialPulseVerifiedBadge user={u} size="lg" showLabel />
 *   <SocialPulseVerifiedBadge user={u} size="xs" />          // icon only
 *
 * We only import the display slug — never anything from the OIDC lib —
 * so this badge stays lightweight and works even when the auth env vars
 * are unset on the current deployment.
 */
import useSite from "@/hooks/useSite";

const SIZE = {
  xs: { icon: 14, gap: 4, label: "text-[10px]" },
  sm: { icon: 16, gap: 5, label: "text-xs" },
  md: { icon: 20, gap: 6, label: "text-sm" },
  lg: { icon: 28, gap: 8, label: "text-base" },
};

/**
 * Does this user carry a SocialPulse identity?
 * Priorities: OIDC sign-in via SocialPulse > registration_source claim >
 * Firebase UID (SocialPulse's canonical auth for the hub).
 */
export function isSocialPulseVerified(user) {
  if (!user || typeof user !== "object") return false;
  // Server-computed flag (privacy-safe, no raw firebase_uid leaked) —
  // trust it first when present.
  if (typeof user.socialpulse_verified === "boolean") return user.socialpulse_verified;
  const provider = (user.auth_provider || "").toString().toLowerCase();
  if (provider.startsWith("socialpulse")) return true;
  const source = (user.registration_source || "").toString().toLowerCase();
  if (source === "socialpulse" || source === "socialpulse.com") return true;
  // Any user with a Firebase UID or a firebase_uids array entry went
  // through SocialPulse's Firebase auth at some point — they qualify.
  if (user.firebase_uid) return true;
  if (Array.isArray(user.firebase_uids) && user.firebase_uids.length > 0) return true;
  return false;
}

export default function SocialPulseVerifiedBadge({
  user,
  size = "sm",
  showLabel = false,
  className = "",
  title = "Verified via SocialPulse — one identity across all sister sites.",
}) {
  // Don't render on the SocialPulse hub itself — every user is a hub
  // user so the badge would be visual noise.
  const { slug } = useSite();
  if (slug === "socialpulse") return null;
  if (!isSocialPulseVerified(user)) return null;

  const s = SIZE[size] || SIZE.sm;
  return (
    <span
      role="img"
      aria-label="Verified via SocialPulse"
      title={title}
      data-testid="socialpulse-verified-badge"
      className={
        "inline-flex items-center rounded-full align-middle select-none " +
        className
      }
      style={{ gap: s.gap }}
    >
      <img
        src="/socialpulse-badge.png"
        alt=""
        aria-hidden="true"
        width={s.icon}
        height={s.icon}
        loading="lazy"
        decoding="async"
        style={{
          width: s.icon,
          height: s.icon,
          objectFit: "contain",
          // The badge PNG is already a tight crop of the purple
          // speech-bubble-with-SP glyph — no cropping tricks needed.
          background: "transparent",
        }}
      />
      {showLabel && (
        <span
          className={"font-semibold tracking-tight " + s.label}
          style={{ color: "#6B21A8" }}
        >
          SocialPulse
        </span>
      )}
    </span>
  );
}
